// Phase 9B — useUserIntel hook.
//
// Read-only consumer of user_intel, user_engagement, and user_notes
// tables. Fetches intelligence data for the authenticated user.
//
// Trust contract:
//   - Read via RLS-scoped SELECT. Writes via RPCs only.
//   - Defensive parsing — missing fields render with fallbacks.
//   - No localStorage. No external HTTP.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ─────────────────────────────────────────────────────

export type IntelCategory =
  | "preference"
  | "behavior"
  | "engagement"
  | "risk_profile"
  | "market_affinity"
  | "performance";

export type EngagementLevel =
  | "dormant"
  | "casual"
  | "regular"
  | "active"
  | "power";

export interface UserIntelEntry {
  id: string;
  category: IntelCategory;
  key: string;
  value_text: string | null;
  value_num: number | null;
  value_json: Record<string, unknown>;
  confidence: number;
  source: string;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserEngagement {
  id: string;
  engagement_level: EngagementLevel;
  sessions_7d: number;
  sessions_30d: number;
  actions_7d: number;
  actions_30d: number;
  markets_active: readonly string[];
  favorite_market: string | null;
  avg_session_mins: number;
  streak_days: number;
  last_active_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserNote {
  id: string;
  author: string;
  content: string;
  pinned: boolean;
  tags: readonly string[];
  created_at: string;
}

// ─── State interfaces ──────────────────────────────────────────

export interface UseUserIntelState {
  entries: readonly UserIntelEntry[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export interface UseUserEngagementState {
  engagement: UserEngagement | null;
  loading: boolean;
  error: string | null;
}

export interface UseUserNotesState {
  notes: readonly UserNote[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Parsers ───────────────────────────────────────────────────

function asStr(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function asNum(v: unknown): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function parseIntelEntry(row: Record<string, unknown>): UserIntelEntry {
  return {
    id: String(row.id ?? ""),
    category: (row.category as IntelCategory) ?? "preference",
    key: String(row.key ?? ""),
    value_text: asStr(row.value_text),
    value_num: row.value_num !== null && row.value_num !== undefined
      ? asNum(row.value_num)
      : null,
    value_json: typeof row.value_json === "object" && row.value_json !== null
      ? (row.value_json as Record<string, unknown>)
      : {},
    confidence: asNum(row.confidence),
    source: String(row.source ?? "system"),
    expires_at: asStr(row.expires_at),
    created_at: String(row.created_at ?? ""),
    updated_at: String(row.updated_at ?? ""),
  };
}

function parseEngagement(row: Record<string, unknown>): UserEngagement {
  return {
    id: String(row.id ?? ""),
    engagement_level: (row.engagement_level as EngagementLevel) ?? "casual",
    sessions_7d: asNum(row.sessions_7d),
    sessions_30d: asNum(row.sessions_30d),
    actions_7d: asNum(row.actions_7d),
    actions_30d: asNum(row.actions_30d),
    markets_active: Array.isArray(row.markets_active)
      ? (row.markets_active as string[])
      : [],
    favorite_market: asStr(row.favorite_market),
    avg_session_mins: asNum(row.avg_session_mins),
    streak_days: asNum(row.streak_days),
    last_active_at: asStr(row.last_active_at),
    created_at: String(row.created_at ?? ""),
    updated_at: String(row.updated_at ?? ""),
  };
}

function parseNote(row: Record<string, unknown>): UserNote {
  return {
    id: String(row.id ?? ""),
    author: String(row.author ?? "system"),
    content: String(row.content ?? ""),
    pinned: row.pinned === true,
    tags: Array.isArray(row.tags) ? (row.tags as string[]) : [],
    created_at: String(row.created_at ?? ""),
  };
}

// ─── useUserIntel ──────────────────────────────────────────────

export function useUserIntel(
  category?: IntelCategory,
): UseUserIntelState {
  const { user } = useAuthSession();
  const [entries, setEntries] = useState<readonly UserIntelEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setEntries([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      let resp: {
        data: Record<string, unknown>[] | null;
        error: { message: string } | null;
      };

      if (category) {
        resp = await (
          client.from("user_intel" as never) as unknown as {
            select: (cols: string) => {
              eq: (col: string, val: string) => {
                order: (col: string, opts: { ascending: boolean }) => Promise<{
                  data: Record<string, unknown>[] | null;
                  error: { message: string } | null;
                }>;
              };
            };
          }
        )
          .select("*")
          .eq("category", category)
          .order("updated_at", { ascending: false });
      } else {
        resp = await (
          client.from("user_intel" as never) as unknown as {
            select: (cols: string) => {
              order: (col: string, opts: { ascending: boolean }) => Promise<{
                data: Record<string, unknown>[] | null;
                error: { message: string } | null;
              }>;
            };
          }
        )
          .select("*")
          .order("updated_at", { ascending: false });
      }

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setEntries([]);
        setLoading(false);
        return;
      }

      setEntries(
        (resp.data ?? []).filter((r) => r !== null).map(parseIntelEntry),
      );
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, category, tick]);

  return useMemo(
    () => ({ entries, loading, error, refetch }),
    [entries, loading, error, refetch],
  );
}

// ─── useUserEngagement ─────────────────────────────────────────

export function useUserEngagement(): UseUserEngagementState {
  const { user } = useAuthSession();
  const [engagement, setEngagement] = useState<UserEngagement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setEngagement(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      const resp = await (
        client.from("user_engagement" as never) as unknown as {
          select: (cols: string) => {
            limit: (n: number) => Promise<{
              data: Record<string, unknown>[] | null;
              error: { message: string } | null;
            }>;
          };
        }
      )
        .select("*")
        .limit(1);

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setEngagement(null);
        setLoading(false);
        return;
      }

      const rows = (resp.data ?? []).filter((r): r is Record<string, unknown> => r !== null);
      const first = rows[0];
      setEngagement(first ? parseEngagement(first) : null);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  return useMemo(
    () => ({ engagement, loading, error }),
    [engagement, loading, error],
  );
}

// ─── useUserNotes ──────────────────────────────────────────────

export function useUserNotes(): UseUserNotesState {
  const { user } = useAuthSession();
  const [notes, setNotes] = useState<readonly UserNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setNotes([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    void (async () => {
      const resp = await (
        client.from("user_notes" as never) as unknown as {
          select: (cols: string) => {
            order: (col: string, opts: { ascending: boolean }) => Promise<{
              data: Record<string, unknown>[] | null;
              error: { message: string } | null;
            }>;
          };
        }
      )
        .select("*")
        .order("created_at", { ascending: false });

      if (cancelled) return;

      if (resp.error) {
        setError(resp.error.message);
        setNotes([]);
        setLoading(false);
        return;
      }

      const parsed = (resp.data ?? []).filter((r) => r !== null).map(parseNote);
      // Pinned notes first, then by created_at
      parsed.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return 0;
      });
      setNotes(parsed);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, tick]);

  return useMemo(
    () => ({ notes, loading, error, refetch }),
    [notes, loading, error, refetch],
  );
}
